import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ShieldCheck, Handshake, ArrowRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { startPartnerCheckout } from "../lib/razorpay";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

/**
 * Post-registration Partner activation page.
 * Shows package details, T&C + 7-day refund policy, then invokes Razorpay ₹1,000 checkout.
 * On success → redirects to /partner/dashboard.
 */
export default function PartnerActivate() {
    const [config, setConfig] = useState(null);
    const [me, setMe] = useState(null);
    const [accept, setAccept] = useState(false);
    const [busy, setBusy] = useState(false);
    const { user } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [c, m] = await Promise.all([
                    api.get("/partner/config"),
                    api.get("/partner/me").catch(() => ({ data: null })),
                ]);
                setConfig(c.data);
                setMe(m.data);
                // Already active → redirect straight to dashboard
                if (m.data?.partner_status === "active") {
                    nav("/partner/dashboard", { replace: true });
                }
            } catch {
                /* keep null */
            }
        })();
    }, [nav]);

    const onPay = async () => {
        setBusy(true);
        const res = await startPartnerCheckout({ acceptTerms: accept });
        setBusy(false);
        if (res.ok) nav("/partner/dashboard", { replace: true });
    };

    if (!config) {
        return (
            <div className="min-h-screen bg-navy-950 flex items-center justify-center" data-testid="partner-activate-loading">
                <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
            </div>
        );
    }

    const pkg = config.package;
    const wrongRole = user && user.role !== "gpm_partner";

    return (
        <div className="text-on-image min-h-screen bg-navy-950 grid-pattern px-4 sm:px-6 py-10" data-testid="partner-activate-page">
            <Seo title="Activate your GPM Partner account" description="Complete your GPM Partner onboarding — ₹1,000 for 12-month partner access, branded marketing kit and eligible commission on qualifying business." path="/partner/activate" />
            <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-11 h-11 rounded-xl bg-gold-500/15 border border-gold-500/40 flex items-center justify-center">
                        <Handshake className="w-6 h-6 text-gold-500" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold-500/90 font-extrabold">GPM Partner Program</div>
                        <h1 className="font-heading text-3xl font-bold text-white">Activate your account</h1>
                    </div>
                </div>

                {wrongRole && (
                    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 mb-6 text-sm text-amber-200" data-testid="partner-wrong-role">
                        Your current account role is <b>{user.role}</b>. Only accounts registered as <b>GPM Partner</b> can activate this package. If you'd like to become a Partner, please <a href="/register" className="underline">create a new GPM Partner account</a>.
                    </div>
                )}

                <div className="rounded-3xl glass p-6 sm:p-8 gold-border-glow">
                    <div className="flex items-baseline justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Package</div>
                            <div className="font-heading text-2xl font-bold text-white mt-1">{pkg.name}</div>
                        </div>
                        <div className="text-right">
                            <div className="font-heading text-4xl font-bold text-gold-500">₹{pkg.price_inr.toLocaleString("en-IN")}</div>
                            <div className="text-xs text-white/60">one-time · {Math.round(pkg.duration_days / 30)}-month validity</div>
                        </div>
                    </div>

                    <div className="mt-6">
                        <div className="text-xs uppercase tracking-widest text-white/60 font-bold mb-3">What's included</div>
                        <ul className="space-y-2.5">
                            {pkg.benefits.map((b, i) => (
                                <li key={i} className="flex items-start gap-3 text-sm text-white/85" data-testid={`partner-benefit-${i}`}>
                                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-6 rounded-xl border border-white/10 bg-navy-900/60 p-4">
                        <div className="flex items-center gap-2 text-emerald-300 font-bold text-sm">
                            <ShieldCheck className="w-4 h-4" />
                            {pkg.refund_window_days}-day refund policy
                        </div>
                        <p className="text-xs text-white/70 mt-2 leading-relaxed">
                            You can request a 100% refund within {pkg.refund_window_days} days of activation <b>if</b> you have not yet downloaded the marketing kit and no partner-attributed leads have been recorded. After that, the package is non-refundable.
                        </p>
                    </div>

                    <div className="mt-4 rounded-xl border border-white/10 bg-navy-900/60 p-4 text-xs text-white/70 leading-relaxed">
                        {pkg.compliance_notes.map((note, i) => (
                            <p key={i} className={i > 0 ? "mt-2" : ""}>• {note}</p>
                        ))}
                    </div>

                    <label className="flex items-start gap-3 mt-6 cursor-pointer" data-testid="partner-accept-tnc-label">
                        <input
                            type="checkbox"
                            checked={accept}
                            onChange={(e) => setAccept(e.target.checked)}
                            className="mt-0.5 w-4 h-4 rounded accent-gold-500"
                            data-testid="partner-accept-tnc"
                        />
                        <span className="text-sm text-white/80 leading-relaxed">
                            I have read and accept the <a href="/terms" target="_blank" rel="noreferrer" className="text-gold-500 underline">GPM Terms &amp; Conditions</a> and the 7-day refund policy above. I understand that eligible commission is based on qualifying business activity and is subject to GPM verification.
                        </span>
                    </label>

                    <button
                        onClick={onPay}
                        disabled={busy || !accept || wrongRole}
                        className="mt-6 w-full h-12 rounded-xl bg-gold-500 hover:bg-gold-400 disabled:opacity-50 disabled:cursor-not-allowed text-navy-950 font-bold transition-colors flex items-center justify-center gap-2"
                        data-testid="partner-pay-btn"
                    >
                        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Pay ₹{pkg.price_inr.toLocaleString("en-IN")} & Activate <ArrowRight className="w-4 h-4" /></>}
                    </button>
                    <p className="text-center text-[11px] text-white/50 mt-3">Secure payment · Powered by Razorpay · UPI / Cards / Netbanking</p>
                </div>
            </div>
        </div>
    );
}
